import { Story } from 'backend';
import classNames from 'classnames/bind';
import Color from 'color';
import parse, {
  HTMLReactParserOptions,
  attributesToProps,
  domToReact,
} from 'html-react-parser';
import { useRouter } from 'next/router';
import { CSSProperties } from 'react';
import { Button, Img } from '..';
import styles from './index.module.scss';

declare global {
  interface Window {
    heroBounds: number;
  }
}

export type LinkProps = Story & {
  onTrack: (type: string, data: any) => void;
};

export const Link = ({
  id,
  action_title,
  action_text,
  action_image,
  action_theme,
  action_color_background,
  action_color_text,
  action_button_text,
  action_button_background,
  action_link,
  action_link_type,
  onTrack,
  ...rest
}: LinkProps) => {
  const cx = classNames.bind(styles);

  const theme = action_color_background
    ? Color(action_color_background).isLight()
      ? 'light'
      : 'dark'
    : 'dark';
  const link = action_link ?? null;
  const { query } = useRouter();
  const params = new URLSearchParams(JSON.parse(JSON.stringify(query)));
  let url = `/${id}/outbound?url=${
    link ? encodeURIComponent(link.replace(/ /g, '')) : null
  }&${params.toString()}`;

  if (link?.startsWith('mailto:')) {
    url = link;
  }

  const options: HTMLReactParserOptions = {
    replace: (domNode: any) => {
      if (
        domNode.name === 'a' &&
        domNode.attribs &&
        !domNode.attribs.href.startsWith('mailto:')
      ) {
        const props = attributesToProps(domNode.attribs);
        return (
          <a
            {...props}
            href={`/${id}/outbound?url=${domNode.attribs.href}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            {domToReact(domNode.children, options)}
          </a>
        );
      }
    },
  };

  const onMailtoClick = async (e: any) => {
    e.preventDefault();

    (await onTrack) &&
      onTrack('outbound', {
        story: id,
        data: {
          url: link,
        },
      });

    if (link) window.open(link, '_self');
  };

  return (
    <a
      href={url}
      className={cx(
        'action',
        theme,
        { color: !!action_color_background },
        'component',
        { ...rest },
      )}
      style={
        {
          backgroundColor: action_color_background,
          '--bg': action_color_background ?? undefined,
          '--text':
            action_color_text ||
            (action_color_background && Color(action_color_background).isLight()
              ? '#000000'
              : '#ffffff'),
        } as CSSProperties
      }
      target="_blank"
      rel="nofollow noreferrer"
      onClick={link?.startsWith('mailto:') ? onMailtoClick : undefined}
    >
      {action_image && (
        <div className={styles.image}>
          <Img src={action_image} fill alt="" />
        </div>
      )}

      <div className={styles.content}>
        <div className={styles.text}>
          <h3>
            {parse(action_title?.replace(/(?:\r\n|\r|\n)/g, '<br>') ?? '')}
          </h3>
          {action_text && (
            <>
              {(action_text ?? '').indexOf('<p>') > -1 &&
                parse(action_text, options)}
              {(action_text ?? '').indexOf('<p>') === -1 && (
                <p>{parse(action_text, options)}</p>
              )}
            </>
          )}
        </div>

        <div className={styles.links}>
          <Button
            arrow
            style={{ backgroundColor: action_button_background ?? undefined }}
            text={action_button_text ?? undefined}
            diap={
              (action_button_background &&
                Color(action_button_background).isLight()) ||
              false
            }
          />
        </div>
      </div>
    </a>
  );
};
